import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { StudioDocument } from '../core/model'

export interface PreviewMetric {
  renderMs: number
  nodeCount: number
  revision: number
}

interface SandboxPreviewProps {
  document: StudioDocument
  selectedNodeId: string
  onSelect: (nodeId: string) => void
  onMetric: (metric: PreviewMetric) => void
  onRuntimeError: (message: string | null) => void
}

function createSandboxSource(channel: string): string {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; script-src 'unsafe-inline'; font-src 'none'; connect-src 'none';" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      * { box-sizing: border-box; }
      html, body, #root { width: 100%; height: 100%; margin: 0; }
      body { overflow: auto; background: #f3f1eb; }
      [data-node-id] { transition: outline-color 120ms ease, filter 120ms ease, transform 160ms ease; }
      [data-node-id]:hover { outline: 1px dashed rgba(49, 102, 255, .55); outline-offset: 2px; }
      [data-selected="true"] { outline: 2px solid #3166ff !important; outline-offset: 3px !important; }
      button:hover { transform: translateY(-1px); filter: brightness(.96); }
      button:focus-visible { outline: 3px solid rgba(49, 102, 255, .42); outline-offset: 2px; }
      @media (max-width: 520px) {
        [data-node-id="page"] { padding: 20px 18px 22px !important; min-height: 100% !important; }
        [data-node-id="navigation"] { margin-bottom: 40px !important; }
        [data-node-id="navStatus"] { display: none !important; }
        [data-node-id="headline"] { font-size: 52px !important; letter-spacing: -.067em !important; }
        [data-node-id="description"] { font-size: 15px !important; margin-top: 19px !important; }
        [data-node-id="actions"] { width: 100% !important; display: grid !important; grid-template-columns: 1fr !important; margin-top: 24px !important; }
        [data-node-id="actions"] button { width: 100% !important; min-height: 46px !important; }
        [data-node-id="signal"] { margin-top: 16px !important; }
        [data-node-id="stats"] { grid-template-columns: 1fr !important; padding-top: 28px !important; }
        [data-node-kind="metric"] { display: flex !important; align-items: baseline !important; justify-content: space-between !important; padding: 13px 15px !important; }
      }
      @media (prefers-reduced-motion: reduce) {
        * { transition-duration: 0.01ms !important; }
      }
    </style>
  </head>
  <body>
    <div id="root"></div>
    <script>
      (() => {
        const CHANNEL = ${JSON.stringify(channel)};
        const ALLOWED_TAGS = new Set(['main', 'header', 'section', 'div', 'span', 'h1', 'p', 'button']);
        const root = document.getElementById('root');
        let revision = 0;

        const send = (type, payload = {}) => {
          parent.postMessage({ channel: CHANNEL, type, ...payload }, '*');
        };

        const build = (documentGraph, nodeId) => {
          const node = documentGraph.nodes[nodeId];
          if (!node) throw new Error('Missing preview node: ' + nodeId);
          if (!ALLOWED_TAGS.has(node.tag)) throw new Error('Blocked element: <' + node.tag + '>');
          const element = document.createElement(node.tag);
          element.dataset.nodeId = node.id;
          element.dataset.nodeKind = node.kind;
          if (node.text) element.append(document.createTextNode(node.text));
          for (const [property, value] of Object.entries(node.styles || {})) {
            try { element.style[property] = String(value); } catch { /* isolated invalid style */ }
          }
          for (const childId of node.children || []) element.append(build(documentGraph, childId));
          return element;
        };

        const render = (payload) => {
          const startedAt = performance.now();
          const nextRoot = build(payload.document, payload.document.rootId);
          root.replaceChildren(nextRoot);
          const selected = root.querySelector('[data-node-id="' + CSS.escape(payload.selectedNodeId) + '"]');
          if (selected) selected.dataset.selected = 'true';
          revision += 1;
          send('rendered', {
            renderMs: performance.now() - startedAt,
            nodeCount: Object.keys(payload.document.nodes).length,
            revision,
          });
        };

        addEventListener('message', (event) => {
          if (event.data?.channel !== CHANNEL || event.data?.type !== 'render') return;
          try {
            render(event.data.payload);
          } catch (error) {
            send('runtime-error', { message: error instanceof Error ? error.message : 'Sandbox render failed.' });
          }
        });

        root.addEventListener('pointerdown', (event) => {
          const target = event.target.closest?.('[data-node-id]');
          if (!target) return;
          event.preventDefault();
          event.stopPropagation();
          send('select', { nodeId: target.dataset.nodeId });
        });

        addEventListener('error', (event) => send('runtime-error', { message: event.message }));
        send('ready');
      })();
    </script>
  </body>
</html>`
}

export function SandboxPreview({
  document,
  selectedNodeId,
  onSelect,
  onMetric,
  onRuntimeError,
}: SandboxPreviewProps) {
  const frameRef = useRef<HTMLIFrameElement>(null)
  const latestPayload = useRef({ document, selectedNodeId })
  const channel = useMemo(
    () =>
      `driftless-${
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2)
      }`,
    [],
  )
  const source = useMemo(() => createSandboxSource(channel), [channel])

  useEffect(() => {
    latestPayload.current = { document, selectedNodeId }
  }, [document, selectedNodeId])

  const sendDocument = useCallback(() => {
    frameRef.current?.contentWindow?.postMessage(
      {
        channel,
        type: 'render',
        payload: latestPayload.current,
      },
      '*',
    )
  }, [channel])

  useEffect(() => {
    sendDocument()
  }, [document, selectedNodeId, sendDocument])

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (
        event.source !== frameRef.current?.contentWindow ||
        event.data?.channel !== channel
      ) {
        return
      }

      if (event.data.type === 'ready') {
        onRuntimeError(null)
        sendDocument()
      }
      if (event.data.type === 'select' && event.data.nodeId) {
        onSelect(event.data.nodeId)
      }
      if (event.data.type === 'rendered') {
        onRuntimeError(null)
        onMetric({
          renderMs: event.data.renderMs,
          nodeCount: event.data.nodeCount,
          revision: event.data.revision,
        })
      }
      if (event.data.type === 'runtime-error') {
        onRuntimeError(event.data.message ?? 'Sandbox render failed.')
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [channel, onMetric, onRuntimeError, onSelect, sendDocument])

  return (
    <iframe
      ref={frameRef}
      className="sandbox-frame"
      title="Isolated application preview"
      sandbox="allow-scripts"
      srcDoc={source}
      onLoad={sendDocument}
    />
  )
}
